import { BrandByKey } from '../contexts/BrandsProvider';
import { ProductGroupById, ProductGroupByKey } from '../contexts/ProductGroupsProvider';
import {
    ProductApplicationById,
    ProductApplicationByKey,
} from '../contexts/ProductApplicationsProvider';
import { ResourceTypeById, ResourceTypeByKey } from '../contexts/ResourceTypesProvider';
import {
    NavigationNodeById,
    NavigationNodeByPath,
} from '../../siteNavigation/contexts/NavigationNodesProvider';

/**
 * How empty filters behave:
 * - `'all'`: return all items regardless of emptiness
 * - `'onlyEmpty'`: return only items that are empty
 */
type EmptyFilterMatchesItems = 'all' | 'onlyEmpty';

type FilterDescriptor = {
    isArray: boolean;
    emptyMeans: EmptyFilterMatchesItems;
};

// Define filter descriptors
export const filterDescriptors = {
    brandId: { isArray: false, emptyMeans: 'all' },
    productGroupIds: { isArray: true, emptyMeans: 'all' },
    productApplicationIds: { isArray: true, emptyMeans: 'all' },
    resourceTypeIds: { isArray: true, emptyMeans: 'all' },
    // navigationScopeIds.emptyMeans was originaly 'onlyEmpty', changed to 'all'
    // (https://www.wrike.com/open.htm?id=1742131586)
    navigationScopeIds: { isArray: true, emptyMeans: 'all' },
    exclusiveNavigationScopeIds: { isArray: true, emptyMeans: 'onlyEmpty' },
    assetPaths: { isArray: true, emptyMeans: 'all' },
    searchTerm: { isArray: false, emptyMeans: 'all' },
} as const satisfies Record<string, FilterDescriptor>;

type Descriptors = typeof filterDescriptors;

/** Filter keys where an empty filter matches only items with an empty value. */
export const emptyFilterMatchesOnlyEmptyKeys: readonly (keyof Descriptors)[] = Object.entries(
    filterDescriptors as Record<string, FilterDescriptor>
)
    .filter(([, d]) => d.emptyMeans === 'onlyEmpty')
    .map(([k]) => k as keyof Descriptors);

export type Filters = Partial<{
    [K in keyof Descriptors]: Descriptors[K]['isArray'] extends true ? string[] : string | null;
}>;

export type FilterName = keyof Filters;

export type CategoryByIdResolvers = {
    // brandById?: BrandById;  // not needed
    productGroupById?: ProductGroupById;
    productApplicationById?: ProductApplicationById;
    resourceTypeById?: ResourceTypeById;
    navigationNodeById?: NavigationNodeById;
    exclusiveNavigationNodeById?: NavigationNodeById;
};

type CategoryIdFilterToResolverKeyMap = {
    // brandId: 'brandById';  // not needed
    productGroupIds: 'productGroupById';
    productApplicationIds: 'productApplicationById';
    resourceTypeIds: 'resourceTypeById';
    navigationScopeIds: 'navigationNodeById';
    exclusiveNavigationScopeIds: 'navigationNodeById';
};

// Helper to enforce that all keys used in `Filters` have Resolvers.
export type MatchingCategoryByIdResolvers<F extends Filters> = {
    [K in keyof F &
        keyof CategoryIdFilterToResolverKeyMap as CategoryIdFilterToResolverKeyMap[K]]-?: CategoryByIdResolvers[CategoryIdFilterToResolverKeyMap[K]];
};

export const getCategoryByIdResolverLookup = <K extends keyof CategoryByIdResolvers>(
    resolvers: MatchingCategoryByIdResolvers<Filters>,
    key: K
): CategoryByIdResolvers[K] | undefined => {
    return (resolvers as Partial<CategoryByIdResolvers>)[key];
};

/**
 * Filter keys for the resource library instead of using numeric IDs.
 *
 * This is used to pass filters to PrefilteredResourceLibraryItemsProvider. Each key is a string
 * of the resource library item's system name. For hierarchical objects (product group, product
 * application, resource type), the key can be `<parent key>/<child key>`.
 */
export type KeyFilters = Partial<{
    brandKey?: string | null;
    productGroupKeys?: string[];
    productApplicationKeys?: string[];
    resourceTypeKeys?: string[];
    navigationScopePaths?: string[];
    exclusiveNavigationScopePaths?: string[];
    resourceAssetPaths?: string[];
    searchTerm?: string | null;
}>;

export type CategoryByKeyResolvers = {
    brandByKey?: BrandByKey;
    productGroupByKey?: ProductGroupByKey;
    productApplicationByKey?: ProductApplicationByKey;
    resourceTypeByKey?: ResourceTypeByKey;
    navigationNodeByPath?: NavigationNodeByPath;
    exclusiveNavigationNodeByPath?: NavigationNodeByPath;
};

// helper map from KeyFilters keys to CategoryByKeyResolvers keys
type CategoryKeyFilterToResolverKeyMap = {
    brandKey: 'brandByKey';
    productGroupKeys: 'productGroupByKey';
    productApplicationKeys: 'productApplicationByKey';
    resourceTypeKeys: 'resourceTypeByKey';
    navigationScopePaths: 'navigationNodeByPath';
    exclusiveNavigationScopePaths: 'navigationNodeByPath';
};

/** Helper to enforce that all keys used in `KeyFilters` have Resolvers */
export type MatchingCategoryByKeyFilterResolvers<F extends KeyFilters> = {
    [K in keyof F &
        keyof CategoryKeyFilterToResolverKeyMap as CategoryKeyFilterToResolverKeyMap[K]]-?: CategoryByKeyResolvers[CategoryKeyFilterToResolverKeyMap[K]];
};

export const getCategoryByKeyResolverLookup = <K extends keyof CategoryByKeyResolvers>(
    resolvers: MatchingCategoryByKeyFilterResolvers<KeyFilters>,
    key: K
): CategoryByKeyResolvers[K] | undefined => {
    return (resolvers as Partial<CategoryByKeyResolvers>)[key];
};
